var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import ndarray from "ndarray";
import regl_ from "regl";
import { MAX_REPEATS_X, MAX_REPEATS_Y } from "./constants";
import { float as f, range } from "./utils";
/**
 * Compute a density heatmap.
 * @param data The time series data as an ndarray.
 * @param binX Configuration for the binning along the time dimension.
 * @param binY Configuration for the binning along the value dimension.
 * @param canvas The canvas for the webgl context and for debug output.
 */
export default function (data, binX, binY, canvas) {
    return __awaiter(this, void 0, void 0, function () {
        function colorMask(i) {
            var mask = [false, false, false, false];
            mask[i % 4] = true;
            return mask;
        }
        var _a, numSeries, numDataPoints, debugCanvas, heatmapWidth, heatmapHeight, regl, maxRenderbufferSize, maxRepeatsX, maxRepeatsY, repeatsX, repeatsY, reshapedWidth, reshapedHeight, drawLine, computeBase, sum, normalize, mergeBufferHorizontally, mergeBufferVertically, drawTexture, linesBuffer, sumsBuffer, resultBuffer, preMergedBuffer, heatBuffer, times, batchSize, series, finishedSeries, b, lines, row, i;
        return __generator(this, function (_b) {
            _a = data.shape, numSeries = _a[0], numDataPoints = _a[1];
            debugCanvas = !!canvas;
            heatmapWidth = Math.floor((binX.stop - binX.start) / binX.step);
            heatmapHeight = Math.floor((binY.stop - binY.start) / binY.step);
            console.info("Heatmap size: " + heatmapWidth + "x" + heatmapHeight);
            regl = regl_({
                canvas: canvas || document.createElement("canvas"),
                extensions: ["OES_texture_float"]
            });
            maxRenderbufferSize = Math.min(regl.limits.maxRenderbufferSize, 4096);
            maxRepeatsX = Math.floor(maxRenderbufferSize / heatmapWidth);
            maxRepeatsY = Math.floor(maxRenderbufferSize / heatmapHeight);
            repeatsX = Math.min(maxRepeatsX, Math.ceil(numSeries / 4 - 1e-6), MAX_REPEATS_X);
            repeatsY = Math.min(maxRepeatsY, Math.ceil(numSeries / (repeatsX * 4)), MAX_REPEATS_Y);
            console.info("Can repeat " + maxRepeatsX + "x" + maxRepeatsY + " times. Repeating " + repeatsX + "x" + repeatsY + " times.");
            reshapedWidth = heatmapWidth * repeatsX;
            reshapedHeight = heatmapHeight * repeatsY;
            console.info("Canvas size " + reshapedWidth + "x" + reshapedHeight + ".");
            drawLine = regl({
                vert: "\n        precision mediump float;\n      \n        attribute float time;\n        attribute float value;\n      \n        uniform float maxX;\n        uniform float maxY;\n        uniform float column;\n        uniform float row;\n      \n        void main() {\n          float repeatsX = " + f(repeatsX) + ";\n          float repeatsY = " + f(repeatsY) + ";\n      \n          // time and value start at 0 so we can simplify the scaling\n          float x = column / repeatsX + time / (maxX * repeatsX);\n          \n          // move up by 0.3 pixels so that the line is guaranteed to be drawn\n          float yOffset = row / repeatsY + 0.3 / " + f(reshapedHeight) + ";\n          // squeeze by 0.6 pixels\n          float squeeze = 1.0 - 0.6 / " + f(heatmapHeight) + ";\n          float yValue = value / (maxY * repeatsY) * squeeze;\n          float y = yOffset + yValue;\n      \n          // squeeze y by 0.3 pixels so that the line is guaranteed to be drawn\n          float yStretch = 2.0 - 0.6 / " + f(reshapedHeight) + ";\n      \n          // scale to [-1, 1]\n          gl_Position = vec4(\n            2.0 * (x - 0.5),\n            2.0 * (y - 0.5),\n            0, 1);\n        }",
                frag: "\n        precision mediump float;\n      \n        void main() {\n          // we will control the color with the color mask\n          gl_FragColor = vec4(1.0, 1.0, 1.0, 1.0);\n        }",
                uniforms: {
                    maxX: regl.prop("maxX"),
                    maxY: regl.prop("maxY"),
                    column: regl.prop("column"),
                    row: regl.prop("row")
                },
                attributes: {
                    time: regl.prop("times"),
                    value: regl.prop("values")
                },
                colorMask: regl.prop("colorMask"),
                depth: { enable: false, mask: false },
                count: regl.prop("count"),
                primitive: "line strip",
                lineWidth: function () { return 1; },
                framebuffer: regl.prop("out")
            });
            computeBase = {
                vert: "\n        precision mediump float;\n      \n        attribute vec2 position;\n        varying vec2 uv;\n      \n        void main() {\n          uv = 0.5 * (position + 1.0);\n          gl_Position = vec4(position, 0, 1);\n        }",
                attributes: {
                    position: [-4, -4, 4, -4, 0, 4]
                },
                depth: { enable: false, mask: false },
                count: 3
            };
            sum = regl(__assign({}, computeBase, { frag: "\n        precision mediump float;\n      \n        uniform sampler2D buffer;\n        varying vec2 uv;\n      \n        void main() {\n          float texelRowStart = floor(uv.y * " + f(repeatsY) + ") / " + f(repeatsY) + ";\n      \n          // normalize by the column\n          vec4 sum = vec4(0.0);\n          for (float j = 0.0; j < " + f(heatmapHeight) + "; j++) {\n            float texelRow = texelRowStart + (j + 0.5) / " + f(reshapedHeight) + ";\n            vec4 value = texture2D(buffer, vec2(uv.x, texelRow));\n            sum += value;\n          }\n      \n          // sum should be at least 1, prevents problems with empty buffers\n          gl_FragColor = max(vec4(1), sum);\n        }", uniforms: {
                    buffer: regl.prop("buffer")
                }, framebuffer: regl.prop("out") }));
            normalize = regl(__assign({}, computeBase, { frag: "\n        precision mediump float;\n      \n        uniform sampler2D buffer;\n        uniform sampler2D sums;\n        varying vec2 uv;\n      \n        void main() {\n          vec4 value = texture2D(buffer, uv);\n          vec4 sum = texture2D(sums, uv);\n      \n          gl_FragColor = value / sum;\n        }", uniforms: {
                    sums: regl.prop("sums"),
                    buffer: regl.prop("buffer")
                }, 
                // additive blending
                blend: {
                    enable: true,
                    func: {
                        srcRGB: "one",
                        srcAlpha: 1,
                        dstRGB: "one",
                        dstAlpha: 1
                    },
                    equation: {
                        rgb: "add",
                        alpha: "add"
                    },
                    color: [0, 0, 0, 0]
                }, framebuffer: regl.prop("out") }));
            mergeBufferHorizontally = regl(__assign({}, computeBase, { frag: "\n        precision mediump float;\n      \n        uniform sampler2D buffer;\n      \n        varying vec2 uv;\n      \n        void main() {\n          vec4 color = vec4(0);\n      \n          // collect all columns\n          for (float i = 0.0; i < " + f(repeatsX) + "; i++) {\n            float x = (i + uv.x) / " + f(repeatsX) + ";\n            color += texture2D(buffer, vec2(x, uv.y));\n          }\n      \n          gl_FragColor = color;\n        }", uniforms: {
                    buffer: regl.prop("buffer")
                }, framebuffer: regl.prop("out") }));
            mergeBufferVertically = regl(__assign({}, computeBase, { frag: "\n        precision mediump float;\n      \n        uniform sampler2D buffer;\n      \n        varying vec2 uv;\n      \n        void main() {\n          vec4 color = vec4(0);\n      \n          // collect all rows\n          for (float i = 0.0; i < " + f(repeatsY) + "; i++) {\n            float y = (i + uv.y) / " + f(repeatsY) + ";\n            color += texture2D(buffer, vec2(uv.x, y));\n          }\n      \n          float value = color.r + color.g + color.b + color.a;\n          gl_FragColor = vec4(vec3(value), 1.0);\n        }", uniforms: {
                    buffer: regl.prop("buffer")
                }, framebuffer: regl.prop("out") }));
            drawTexture = regl(__assign({}, computeBase, { frag: "\n        precision mediump float;\n      \n        uniform sampler2D buffer;\n        \n        varying vec2 uv;\n        \n        void main() {\n          // get r and draw it\n          vec3 value = texture2D(buffer, uv).rgb;\n          gl_FragColor = vec4(value, 1.0);\n        }", colorMask: regl.prop("colorMask"), uniforms: {
                    buffer: regl.prop("buffer")
                } }));
            console.time("Allocate buffers");
            linesBuffer = regl.framebuffer({
                width: reshapedWidth,
                height: reshapedHeight,
                colorFormat: "rgba",
                colorType: "uint8"
            });
            sumsBuffer = regl.framebuffer({
                width: reshapedWidth,
                height: repeatsY,
                colorFormat: "rgba",
                colorType: "float"
            });
            resultBuffer = regl.framebuffer({
                width: reshapedWidth,
                height: reshapedHeight,
                colorFormat: "rgba",
                colorType: "float"
            });
            preMergedBuffer = regl.framebuffer({
                width: heatmapWidth,
                height: reshapedHeight,
                colorFormat: "rgba",
                colorType: "float"
            });
            heatBuffer = regl.framebuffer({
                width: heatmapWidth,
                height: heatmapHeight,
                colorFormat: "rgba",
                colorType: "float"
            });
            console.timeEnd("Allocate buffers");
            times = range(numDataPoints);
            console.time("Compute heatmap");
            batchSize = 4 * repeatsX * repeatsY;
            series = 0;
            finishedSeries = 0;
            for (b = 0; b < numSeries; b += batchSize) {
                console.time("Prepare Batch");
                lines = new Array(Math.min(batchSize, numSeries - series));
                // clear the lines buffer before the next batch
                regl.clear({
                    color: [0, 0, 0, 0],
                    framebuffer: linesBuffer
                });
                loop: for (row = 0; row < repeatsY; row++) {
                    for (i = 0; i < 4 * repeatsX; i++) {
                        if (series >= numSeries) {
                            break loop;
                        }
                        // console.log(series, Math.floor(i / 4), row);
                        lines[series - finishedSeries] = {
                            values: data.pick(series, null),
                            times: times,
                            maxY: binY.stop,
                            maxX: numDataPoints - 1,
                            column: Math.floor(i / 4),
                            row: row,
                            colorMask: colorMask(i),
                            count: numDataPoints,
                            out: linesBuffer
                        };
                        series++;
                    }
                }
                console.timeEnd("Prepare Batch");
                console.info("Drawing " + lines.length + " lines.");
                console.time("regl: drawLine");
                drawLine(lines);
                console.timeEnd("regl: drawLine");
                finishedSeries += lines.length;
                console.time("regl: sum");
                sum({
                    buffer: linesBuffer,
                    out: sumsBuffer
                });
                console.timeEnd("regl: sum");
                console.time("regl: normalize");
                normalize({
                    buffer: linesBuffer,
                    sums: sumsBuffer,
                    out: resultBuffer
                });
                console.timeEnd("regl: normalize");
            }
            console.time("regl: merge");
            mergeBufferHorizontally({
                buffer: resultBuffer,
                out: preMergedBuffer
            });
            mergeBufferVertically({
                buffer: preMergedBuffer,
                out: heatBuffer
            });
            console.timeEnd("regl: merge");
            console.timeEnd("Compute heatmap");
            if (debugCanvas) {
                drawTexture({
                    buffer: resultBuffer,
                    colorMask: [true, true, true, true]
                });
            }
            return [2 /*return*/, new Promise(function (resolve) {
                    regl({ framebuffer: heatBuffer })(function () {
                        var arr = regl.read();
                        var out = new Float32Array(arr.length / 4);
                        for (var i = 0; i < arr.length; i += 4) {
                            out[i / 4] = arr[i];
                        }
                        var heatmap = ndarray(out, [heatmapHeight, heatmapWidth]);
                        var heatmapData = [];
                        for (var x = 0; x < heatmapWidth; x++) {
                            for (var y = 0; y < heatmapHeight; y++) {
                                heatmapData.push({
                                    x: binX.start + x * binX.step,
                                    y: binY.start + y * binY.step,
                                    value: heatmap.get(y, x)
                                });
                            }
                        }
                        resolve(heatmapData);
                    });
                })];
        });
    });
}
